import { BookOpenIcon, BuildingStorefrontIcon, CalendarDaysIcon, ClockIcon, MinusCircleIcon, PauseCircleIcon, PlayCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { FormikConfig } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import { ActionList, ActionListItem } from '../../components/ActionList';
import { Button } from '../../components/Button';
import { InfoContent } from '../../components/InfoContent';
import { CreditBalance } from '../../components/CreditLedger/CreditBalance';
import { SubscriptionHeader } from '../../components/CreditLedger/SubscriptionHeader';
import { Modal } from '../../components/Modal';
import { Tooltip } from '../../components/Tooltip';
import { CreateModal } from '../../components/Modal/CreateModal';
import { FormikDayInput } from '../../components/formik/FormDayInput';
import { FormInput, FormikInput } from '../../components/formik/FormInput';
import { DateValue, useFormatDate } from '../../hooks/useFormatDate';
import { todayUtc } from '../../utils/dates';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import {
  MonthlyAllowance,
  SubscriptionType,
  SuperadminOrganisationFragment,
  useOrganisationBillingWithOdooQuery,
  useOrganisationLinkBillingWithOdooMutation,
  useSuperadminCreateOdooPartnerMutation,
  useSuperadminGrantCreditsMutation,
  useSuperadminSetBillingBlankMutation,
  useSuperadminSetOdooOrderMutation,
  useSuperadminSetOdooPartnerMutation,
} from '../../types/graphqlTypes';
import { BillingInfoCard, BillingInfoModal } from '../organisation/BillingInfoCard';
import { ChangePlanModal, ChangePlanModalProps } from './ChangePlanModal';
import { HistoryModal } from './HistoryModal';
import { LedgerModal } from './LedgerModal';
import { NoBillingDetailsWarning } from './NoBillingDetailsWarning';
import { RemoveCreditsModal } from './RemoveCreditsModal';
import { BillingDateModal, CancelSubscriptionDialog, PauseSubscriptionDialog } from './SubscriptionActions';

// Whatever is on screen: the organisations list, and the history when it is open.
const REFETCH_QUERIES = 'active' as const;

const useTranslations = () => {
  const grantCredits = useT('grant projects', { swc: true });
  const amount = useT('quantity', { swc: true });
  const expiresAt = useT('expiry date (optional)', { swc: true });
  const note = useT('note', { swc: true });
  const unitPrice = useT('price per project (€)', { swc: true });
  return { grantCredits, amount, expiresAt, note, unitPrice };
};

interface GrantFormValues {
  amount: number;
  // An ISO day, or empty: the projects then never expire.
  expiresAt: string;
  note: string;
  unitPrice: number | '';
}

interface GrantCreditsModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  // The organisation's current price per project in cents: the grant's default price.
  projectUnitPrice: number;
  // The organisation has no billing details: projects can still be given, but nothing can be invoiced, so for free.
  priceLocked?: boolean;
  // ... and nobody said that is on purpose: nothing is given until someone does, or fills them in.
  billingUnset?: boolean;
}
const GrantCreditsModal: React.FC<GrantCreditsModalProps> = ({ onClose, open, organisationId, projectUnitPrice, priceLocked, billingUnset }) => {
  const [grantCredits, { loading }] = useSuperadminGrantCreditsMutation({ refetchQueries: REFETCH_QUERIES });
  const translations = useTranslations();
  const { showToast } = useToastNotifications();
  const failed = useT('could not grant the projects', { swc: true });

  const onSubmit: FormikConfig<GrantFormValues>['onSubmit'] = useCallback(
    async (values) => {
      try {
        await grantCredits({
          variables: {
            organisationId,
            amount: Number(values.amount),
            // The day itself: the server makes the projects last until the end of it.
            expiresAt: values.expiresAt ? new Date(values.expiresAt).toISOString() : undefined,
            note: values.note || undefined,
            // Entered in euros, stored in cents; 0 makes the grant free — as does a locked price, whatever the form
            // holds. The server refuses a priced grant to an organisation without billing details either way.
            unitPriceCents: priceLocked || values.unitPrice === '' ? undefined : Math.round(Number(values.unitPrice) * 100),
          },
        });
        onClose();
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [failed, grantCredits, onClose, organisationId, priceLocked, showToast],
  );

  const formik: FormikConfig<GrantFormValues> = useMemo(() => ({ initialValues: { amount: 1, expiresAt: '', note: '', unitPrice: projectUnitPrice / 100 }, onSubmit }), [onSubmit, projectUnitPrice]);

  return (
    <CreateModal title={translations.grantCredits} createButtonTitle={<T _str="submit" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={loading} submitDisabled={billingUnset} showCloseIcon>
      <div className="flex flex-col">
        {billingUnset && (
          <InfoContent type="warning" className="mb-3" title={<T _str="Billing details first" swc />} description={<T _str="Fill in the organisation's billing details, or mark them as left blank on purpose, before giving anything." />} />
        )}
        <FormikInput type="number" min={1} autoFocus name="amount" label={translations.amount} />
        {priceLocked ? (
          <div className="flex flex-col">
            <FormInput name="lockedUnitPrice" label={translations.unitPrice} value="0" disabled readOnly />
            {/* FormInput always reserves an (empty here) error row — pull the warning back up under the control. */}
            <NoBillingDetailsWarning className="-mt-4 mb-4" />
          </div>
        ) : (
          <div className="flex flex-col">
            <FormikInput type="number" min={0} step="0.01" name="unitPrice" label={translations.unitPrice} required />
            {/* FormInput always reserves an (empty here) error row — pull the hint back up under the control. */}
            <span className="-mt-4 mb-4 text-xs text-gray-500">
              <T _str="With a price, the draft invoice is made in Odoo like any other sale. 0 makes the projects free, without an invoice." />
            </span>
          </div>
        )}
        <FormikDayInput name="expiresAt" label={translations.expiresAt} min={todayUtc()} />
        <FormikInput name="note" label={translations.note} />
      </div>
    </CreateModal>
  );
};

interface OdooPartnerFormValues {
  partnerId: number | '';
}

// The Odoo customer an organisation's invoices are made out to, set by hand:
// for customers that predate the registration lead, or to correct a wrong match.
// Left empty, the next invoice finds or makes one again.
const OdooPartnerModal: React.FC<{ open: boolean; onClose: () => void; onChanged: () => void; organisationId: string; partnerId?: number | null }> = ({ open, onClose, onChanged, organisationId, partnerId }) => {
  // Another customer means other billing details: onChanged looks them up again. Not refetchQueries by name —
  // every organisation's card has this modal, and that would look up all the ones opened before too, at once,
  // which Odoo's rate limit refuses. The mutation returns the organisation itself, and the billing lookup
  // returns it again as it leaves it (an emptied link is looked up anew there) — refetching the organisations
  // list alongside would race that lookup and could put the old link back.
  const [setPartner, { loading }] = useSuperadminSetOdooPartnerMutation();
  const { showToast } = useToastNotifications();
  const title = useT('Odoo company', { swc: true });
  const label = useT('Odoo company id', { swc: true });
  const failed = useT('could not save the Odoo company', { swc: true });

  const onSubmit: FormikConfig<OdooPartnerFormValues>['onSubmit'] = useCallback(
    async (values) => {
      try {
        await setPartner({ variables: { organisationId, partnerId: values.partnerId === '' ? null : Number(values.partnerId) } });
        onChanged();
        onClose();
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [failed, onChanged, onClose, organisationId, setPartner, showToast],
  );
  const formik: FormikConfig<OdooPartnerFormValues> = useMemo(() => ({ initialValues: { partnerId: partnerId ?? '' }, onSubmit, enableReinitialize: true }), [onSubmit, partnerId]);

  return (
    <CreateModal title={title} createButtonTitle={<T _str="save" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={loading} showCloseIcon>
      <div className="flex flex-col gap-2">
        <FormikInput type="number" min={1} step={1} name="partnerId" label={label} autoFocus />
        <span className="text-xs text-gray-500">
          <T _str="The id is in the company's URL in Odoo (Contacts). Leave it empty to let the next invoice find or make the company again." />
        </span>
      </div>
    </CreateModal>
  );
};

interface OdooOrderFormValues {
  orderId: number | '';
}

// The subscription order in Odoo billing the running term, set by hand: for the terms the daily matcher could
// not pair with sales' subscription (none, or several, for the customer). Left empty, the term is unlinked.
const OdooOrderModal: React.FC<{ open: boolean; onClose: () => void; organisationId: string; odoo: SuperadminOrganisationFragment['odoo'] }> = ({ open, onClose, organisationId, odoo }) => {
  // The mutation returns the organisation itself (its link and what the matcher found): nothing to refetch.
  const [setOrder, { loading }] = useSuperadminSetOdooOrderMutation();
  const { showToast } = useToastNotifications();
  const title = useT('Odoo subscription', { swc: true });
  const label = useT('Odoo subscription order id', { swc: true });
  const failed = useT('could not save the Odoo subscription', { swc: true });

  const onSubmit: FormikConfig<OdooOrderFormValues>['onSubmit'] = useCallback(
    async (values) => {
      try {
        await setOrder({ variables: { organisationId, orderId: values.orderId === '' ? null : Number(values.orderId) } });
        onClose();
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [failed, onClose, organisationId, setOrder, showToast],
  );
  const formik: FormikConfig<OdooOrderFormValues> = useMemo(() => ({ initialValues: { orderId: odoo.orderId ?? '' }, onSubmit, enableReinitialize: true }), [onSubmit, odoo.orderId]);

  return (
    <CreateModal title={title} createButtonTitle={<T _str="save" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={loading} showCloseIcon>
      <div className="flex flex-col gap-2">
        <FormikInput type="number" min={1} step={1} name="orderId" label={label} autoFocus />
        <span className="text-xs text-gray-500">
          <T _str="The id is in the subscription's URL in Odoo (Subscriptions). The term then ends the day before its next invoice. Leave it empty to unlink." />
        </span>
        {odoo.orderCandidates.length > 0 && (
          <span className="text-xs text-gray-500">
            <T _str="Found for this customer" />:{' '}
            {odoo.orderCandidates.map((candidate, i) => (
              <span key={candidate.id}>
                {i > 0 && ', '}
                {candidate.url ? (
                  <a href={candidate.url} target="_blank" rel="noreferrer" className="underline">
                    #{candidate.id}
                  </a>
                ) : (
                  `#${candidate.id}`
                )}
              </span>
            ))}
          </span>
        )}
      </div>
    </CreateModal>
  );
};

// What Odoo last said about the subscription order, in a word.
const useOrderStateLabels = (): Record<string, string> => {
  const draft = useT('quotation, to be confirmed');
  const running = useT('running');
  const paused = useT('paused');
  const closed = useT('closed');
  const renewal = useT('up for renewal');
  return useMemo(() => ({ '1_draft': draft, '2_renewal': renewal, '3_progress': running, '4_paused': paused, '5_renewed': closed, '6_churn': closed }), [draft, renewal, running, paused, closed]);
};

// What matters in the Odoo row stands out from its labels: the linked company and subscription, and the actions on
// them (in the accent, as the billing card's edit).
const linkedClass = 'font-medium text-black underline dark:text-white';
const actionClass = (busy?: boolean) => classNames('font-medium text-french-violet-600 underline hover:text-french-violet-800 dark:text-french-violet-400 dark:hover:text-french-violet-300', busy && 'cursor-wait opacity-60');

// Where the organisation lives in Odoo: the customer invoices are made out to, the lead its registration
// created, and the subscription order billing the running term.
// `partnerName`: the customer's company name as its billing details hold it, shown in place of the id.
const OdooLinks: React.FC<{ organisationId: string; odoo: SuperadminOrganisationFragment['odoo']; partnerName?: string | null; onPartnerChanged: () => void }> = ({ organisationId, odoo, partnerName, onPartnerChanged }) => {
  const [open, setOpen] = useState(false);
  const onOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);
  const [orderOpen, setOrderOpen] = useState(false);
  const onOpenOrder = useCallback(() => setOrderOpen(true), []);
  const onCloseOrder = useCallback(() => setOrderOpen(false), []);
  const orderStates = useOrderStateLabels();
  const { formatDay } = useFormatDate();
  // Auto link: the link the customer's own order makes (the customer on the registration lead, else one with
  // the owner's email, else a new one for an organisation with a lead), asked for here. The answer carries the
  // billing details and the organisation's Odoo link, so both show by themselves.
  const [linkBilling, { loading: linking }] = useOrganisationLinkBillingWithOdooMutation();
  const { showToast } = useToastNotifications();
  const noMatch = useT('no matching company in Odoo', { swc: true });
  const noMatchText = useT('This organisation has no registration lead to make one from. Set the id by hand, or let the first invoice make the company.');
  const linkFailed = useT('could not link the Odoo company', { swc: true });
  // Create: the customer made from what the organisation is, for one the automatic link makes none for (no
  // registration lead). Finds one first, so it never duplicates.
  const [createPartner, { loading: creating }] = useSuperadminCreateOdooPartnerMutation();
  const createFailed = useT('could not create the Odoo company', { swc: true });
  const onCreate = useCallback(async () => {
    try {
      await createPartner({ variables: { organisationId } });
      onPartnerChanged();
    } catch (e) {
      showToast({ title: createFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [createFailed, createPartner, onPartnerChanged, organisationId, showToast]);
  const onAutoLink = useCallback(async () => {
    try {
      const { data } = await linkBilling({ variables: { organisationId } });
      if (data && !data.organisationLinkBilling.linked) showToast({ title: noMatch, text: noMatchText, type: ToastType.INFO });
    } catch (e) {
      showToast({ title: linkFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [linkBilling, linkFailed, noMatch, noMatchText, organisationId, showToast]);
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
      <T _str="Odoo company" swc />:{' '}
      {odoo.partnerId ? (
        odoo.partnerUrl ? (
          <a href={odoo.partnerUrl} target="_blank" rel="noreferrer" className={linkedClass} title={`#${odoo.partnerId}`}>
            {partnerName || `#${odoo.partnerId}`}
          </a>
        ) : (
          <span className="font-medium text-black dark:text-white" title={`#${odoo.partnerId}`}>
            {partnerName || `#${odoo.partnerId}`}
          </span>
        )
      ) : (
        <>
          <T _str="unlinked" />
          {' · '}
          <button type="button" onClick={onAutoLink} disabled={linking} className={actionClass(linking)}>
            <T _str="auto link" />
          </button>
          {' · '}
          <button type="button" onClick={onCreate} disabled={creating} className={actionClass(creating)}>
            <T _str="create" />
          </button>
        </>
      )}
      {' · '}
      <button type="button" onClick={onOpen} className={actionClass()}>
        {odoo.partnerId ? <T _str="change" /> : <T _str="set" />}
      </button>
      {odoo.leadId && (
        <>
          {' · '}
          <T _str="lead" />{' '}
          {odoo.leadUrl ? (
            <a href={odoo.leadUrl} target="_blank" rel="noreferrer" className="underline">
              #{odoo.leadId}
            </a>
          ) : (
            `#${odoo.leadId}`
          )}
        </>
      )}
      <OdooPartnerModal open={open} onClose={onClose} onChanged={onPartnerChanged} organisationId={organisationId} partnerId={odoo.partnerId} />
      <span className="basis-full" />
      <T _str="Odoo subscription" swc />:{' '}
      {odoo.orderId ? (
        <>
          {odoo.orderUrl ? (
            <a href={odoo.orderUrl} target="_blank" rel="noreferrer" className={linkedClass} title={`#${odoo.orderId}`}>
              {odoo.orderName || `#${odoo.orderId}`}
            </a>
          ) : (
            <span className="font-medium text-black dark:text-white" title={`#${odoo.orderId}`}>
              {odoo.orderName || `#${odoo.orderId}`}
            </span>
          )}
          {odoo.orderState && <> · {orderStates[odoo.orderState] ?? odoo.orderState}</>}
          {odoo.orderConfirmed === false && (
            <>
              {' · '}
              <T _str="not confirmed" />
            </>
          )}
          {odoo.orderEndsAt && (
            <>
              {' · '}
              <T _str="ends {date}" args={{ date: formatDay(odoo.orderEndsAt) }} />
            </>
          )}
          {odoo.orderCloseReason && <> ({odoo.orderCloseReason})</>}
        </>
      ) : (
        <>
          <T _str="unlinked" />
          {odoo.orderLinkStatus === 'NO_MATCH' && (
            <>
              {' · '}
              <T _str="no running subscription found for the company" />
            </>
          )}
          {odoo.orderLinkStatus === 'AMBIGUOUS' && (
            <>
              {' · '}
              <T _str="{count} found for the company, pick one" args={{ count: odoo.orderCandidates.length }} />
            </>
          )}
        </>
      )}
      {' · '}
      <button type="button" onClick={onOpenOrder} className={actionClass()}>
        {odoo.orderId ? <T _str="change" /> : <T _str="set" />}
      </button>
      <OdooOrderModal open={orderOpen} onClose={onCloseOrder} organisationId={organisationId} odoo={odoo} />
    </div>
  );
};

export interface CreditsModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  odoo: SuperadminOrganisationFragment['odoo'];
  organisationName: string;
  balance: number;
  nextExpiry?: DateValue | null;
  // Price per project in cents, the default for granted credits.
  projectUnitPrice: number;
  subscriptionType: SubscriptionType;
  scheduledSubscriptionType?: SubscriptionType | null;
  scheduledAt?: DateValue | null;
  subscriptionStartDate?: DateValue | null;
  subscriptionEndDate?: DateValue | null;
  subscriptionPausedAt?: DateValue | null;
  monthlyAllowance?: Pick<MonthlyAllowance, 'total' | 'remaining' | 'renewsAt'> | null;
  // Seeds the plan form with the organisation's current plan and custom limits.
  planInitialValues: ChangePlanModalProps['initialValues'];
  // The partner Pythagoras invoices for this organisation, if any: its invoices are made out to that partner.
  billedTo?: { name: string } | null;
}
// The superadmin's one stop for an organisation's subscription: plan, allowance,
// balance, purchase history, and the plan and grant actions.
const CreditsModal_: React.FC2<CreditsModalProps> = ({
  onClose,
  open,
  organisationId,
  organisationName,
  balance,
  nextExpiry,
  projectUnitPrice,
  subscriptionType,
  scheduledSubscriptionType,
  scheduledAt,
  subscriptionStartDate,
  subscriptionEndDate,
  subscriptionPausedAt,
  monthlyAllowance,
  planInitialValues,
  odoo,
  billedTo,
}) => {
  const { formatDay } = useFormatDate();
  // Nothing is sold to a customer that cannot be invoiced: while the billing details are incomplete a grant
  // still goes ahead, but at a price locked to 0 (the server refuses a priced one either way); a plan change
  // asks the server, which locks its price by itself. Only a definite "incomplete" locks — Odoo being off or
  // unreachable does not.
  // With the organisation's Odoo link as the lookup left it, so the link shown above is the one the details came from.
  // Billed to a partner: the partner is invoiced, so its billing details are the ones that count (the server checks
  // them); the organisation's own are not asked for, and hold nothing back.
  const { data: billingData, error: billingError, refetch: refetchBillingQuery } = useOrganisationBillingWithOdooQuery({ variables: { organisationId }, skip: !open || !!billedTo, fetchPolicy: 'cache-and-network' });
  // Only this organisation's lookup, after its customer or billing details changed. A failure shows as billingError.
  const refetchBilling = useCallback(() => void refetchBillingQuery().catch(() => undefined), [refetchBillingQuery]);
  // A failed lookup leaves the last result in place, which may be another customer's (the link was just changed): show nothing instead.
  const billing = billingError || billedTo ? undefined : billingData?.organisationBilling;
  const billingMissing = !!billing?.enabled && !billing.complete;
  // Incomplete and nobody said so on purpose: nothing is sold or given until someone does (the server refuses too).
  const billingUnset = billingMissing && !billing?.blank;
  // Confirmed blank, or taken back: the answer is the billing itself, the card reads it from the cache.
  const [setBillingBlank] = useSuperadminSetBillingBlankMutation();
  const { showToast: showBlankToast } = useToastNotifications();
  const blankFailed = useT('could not save the billing details state', { swc: true });
  const setBlank = useCallback(
    async (blank: boolean) => {
      try {
        await setBillingBlank({ variables: { organisationId, blank } });
      } catch (e) {
        showBlankToast({ title: blankFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [blankFailed, organisationId, setBillingBlank, showBlankToast],
  );
  const onLeaveBlank = useCallback(() => void setBlank(true), [setBlank]);
  const onUndoBlank = useCallback(() => void setBlank(false), [setBlank]);
  // While nobody looked at the billing details, what would sell or give is held back, and says why on hover.
  // Paused, the account is frozen: nothing but resuming it, or ending it, until then.
  const paused = !!subscriptionPausedAt;
  const pausedReason = paused ? <T _str="The subscription is paused. Resume it first. Only cancelling is possible meanwhile." /> : undefined;
  const billingUnsetReason = pausedReason ?? (billingUnset ? <T _str="Fill in the billing details first, or mark them as left blank on purpose." /> : undefined);
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const openBillingModal = useCallback(() => setBillingModalOpen(true), []);
  const closeBillingModal = useCallback(() => setBillingModalOpen(false), []);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const openRemoveModal = useCallback(() => setRemoveModalOpen(true), []);
  const closeRemoveModal = useCallback(() => setRemoveModalOpen(false), []);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const openHistoryModal = useCallback(() => setHistoryModalOpen(true), []);
  const closeHistoryModal = useCallback(() => setHistoryModalOpen(false), []);
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const openLedgerModal = useCallback(() => setLedgerModalOpen(true), []);
  const closeLedgerModal = useCallback(() => setLedgerModalOpen(false), []);

  const openGrantModal = useCallback(() => setGrantModalOpen(true), []);
  const closeGrantModal = useCallback(() => setGrantModalOpen(false), []);
  const openPlanModal = useCallback(() => setPlanModalOpen(true), []);
  const closePlanModal = useCallback(() => setPlanModalOpen(false), []);
  const [billingDateModalOpen, setBillingDateModalOpen] = useState(false);
  const openBillingDateModal = useCallback(() => setBillingDateModalOpen(true), []);
  const closeBillingDateModal = useCallback(() => setBillingDateModalOpen(false), []);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const openPauseDialog = useCallback(() => setPauseDialogOpen(true), []);
  const closePauseDialog = useCallback(() => setPauseDialogOpen(false), []);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const openCancelDialog = useCallback(() => setCancelDialogOpen(true), []);
  const closeCancelDialog = useCallback(() => setCancelDialogOpen(false), []);

  // The actions of a running term: a paid plan whose end has not passed. Pay-as-you-go has no term to date,
  // pause or end.
  const hasTerm = subscriptionType !== SubscriptionType.Flex && !!subscriptionEndDate && new Date(subscriptionEndDate) > new Date();
  const ending = scheduledSubscriptionType === SubscriptionType.Flex;
  const odooOrderLinked = !!odoo.orderId;

  return (
    <>
      <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg">
        <Modal.Header onClose={onClose}>
          <span>
            <T _str="subscription" swc /> - {organisationName}
          </span>
        </Modal.Header>
        <Modal.Body className="flex flex-col gap-3 px-1">
          {/* The plan is changed from the plan itself: the card opens the change. */}
          <SubscriptionHeader
            type={subscriptionType}
            scheduledType={scheduledSubscriptionType}
            scheduledAt={scheduledAt}
            startDate={subscriptionStartDate}
            endDate={subscriptionEndDate}
            pausedAt={subscriptionPausedAt}
            monthlyAllowance={monthlyAllowance}
            onChangePlan={openPlanModal}
            changePlanDisabledReason={billingUnsetReason}
          />
          {billedTo ? (
            <div className="flex items-start gap-3 rounded-xl border border-neon-green-500 bg-neon-green-500/10 p-4 text-sm">
              <BuildingStorefrontIcon className="h-6 w-6 shrink-0 text-dark-green-700 dark:text-neon-green-300" />
              <div className="flex flex-col gap-1">
                <span className="font-semibold">
                  <T _str="Billed to {partner}" args={{ partner: billedTo.name }} />
                </span>
                <span className="text-gray-600 dark:text-gray-300">
                  <T
                    _str="Everything sold to this organisation (plan changes, renewals, projects, also what you do here) is invoiced to {partner}, with the partner's discount taken off each line. The partner's billing details apply. This organisation orders nothing itself."
                    args={{ partner: billedTo.name }}
                  />
                </span>
              </div>
            </div>
          ) : null}
          {/* Billed to a partner too: its own Odoo company (the end customer on the partner's orders) and its subscription,
              made out to the partner. */}
          <OdooLinks organisationId={organisationId} odoo={odoo} partnerName={billing?.linked ? billing.info?.companyName : undefined} onPartnerChanged={refetchBilling} />
          {/* Right under the customer they belong to, and above the sales they hold back. No customer linked yet: link one
              first (auto link / set, above) rather than make a new one in Odoo by filling in details. Billed to a partner,
              the partner's details apply, none of its own. */}
          {!billedTo && <BillingInfoCard billing={billing} onEdit={openBillingModal} addDisabledReason={odoo.partnerId ? undefined : <T _str="Link an Odoo company first." />} onLeaveBlank={onLeaveBlank} onUndoBlank={onUndoBlank} />}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col">
              <CreditBalance balance={balance} monthlyRemaining={monthlyAllowance?.remaining} />
              {nextExpiry && (
                <span className="text-xs text-gray-500">
                  <T _str="next expiry" swc />: {formatDay(nextExpiry)}
                </span>
              )}
            </div>
            {/* The one primary action, next to the number it changes. Never held back in the admin panel: without
                billing details it simply cannot carry a price (see priceLocked below). */}
            {billingUnsetReason ? (
              <Tooltip message={<span>{billingUnsetReason}</span>} delay={0}>
                <Button variant="primary" size="sm" disabled>
                  <T _str="grant projects" swc />
                </Button>
              </Tooltip>
            ) : (
              <Button variant="primary" size="sm" onClick={openGrantModal}>
                <T _str="grant projects" swc />
              </Button>
            )}
          </div>
          {/* The rest, each in a window of its own, as on the customer's subscription page (the plan itself is
              changed from its card above): what shapes the subscription and its history first, then what takes
              something away — pausing blocks the whole account, the worst of them, so it sits with the other
              red ones. */}
          <ActionList>
            {hasTerm && !paused && (
              <ActionListItem icon={CalendarDaysIcon} title={<T _str="change billing date" swc />} description={<T _str="Move the renewal day, in Odoo too" />} onClick={openBillingDateModal} disabledReason={billingUnsetReason} />
            )}
            <ActionListItem icon={ClockIcon} title={<T _str="purchase history" swc />} description={<T _str="Every grant and fee, with its invoice" />} onClick={openHistoryModal} />
            <ActionListItem icon={BookOpenIcon} title={<T _str="view ledger" swc />} description={<T _str="Every entry, project charges included" />} onClick={openLedgerModal} />
            {hasTerm && (
              <ActionListItem
                icon={XCircleIcon}
                title={<T _str="cancel subscription" swc />}
                description={paused ? <T _str="End it now: the term is voided and its projects are taken away" /> : ending ? <T _str="Already ending with the term. End it now instead" /> : <T _str="At the end of the term, or now" />}
                onClick={openCancelDialog}
                danger
              />
            )}
            {hasTerm && (
              <ActionListItem
                icon={paused ? PlayCircleIcon : PauseCircleIcon}
                title={paused ? <T _str="resume subscription" swc /> : <T _str="pause subscription" swc />}
                description={paused ? <T _str="Paused since {date}. Uploads and orders work again" args={{ date: formatDay(subscriptionPausedAt) }} /> : <T _str="Temporarily suspend the subscription. The renewal date stays the same." />}
                onClick={openPauseDialog}
                danger={!paused}
              />
            )}
            <ActionListItem icon={MinusCircleIcon} title={<T _str="remove projects" swc />} description={<T _str="Take unspent projects off the balance" />} onClick={openRemoveModal} danger disabledReason={pausedReason} />
          </ActionList>
        </Modal.Body>
      </Modal.Container>
      <LedgerModal open={ledgerModalOpen} onClose={closeLedgerModal} organisationId={organisationId} organisationName={organisationName} />
      <HistoryModal open={historyModalOpen} onClose={closeHistoryModal} organisationId={organisationId} organisationName={organisationName} />
      {/* No billing details, nobody to invoice: both still work, at a price locked to 0 and with a warning saying why. */}
      <GrantCreditsModal open={grantModalOpen} onClose={closeGrantModal} organisationId={organisationId} projectUnitPrice={projectUnitPrice} priceLocked={billingMissing} billingUnset={billingUnset} />
      <ChangePlanModal open={planModalOpen} onClose={closePlanModal} organisationId={organisationId} odooOrderLinked={odooOrderLinked} initialValues={planInitialValues} />
      <BillingDateModal open={billingDateModalOpen} onClose={closeBillingDateModal} organisationId={organisationId} endDate={subscriptionEndDate} />
      <PauseSubscriptionDialog open={pauseDialogOpen} onClose={closePauseDialog} organisationId={organisationId} pausedAt={subscriptionPausedAt} odooLinked={odooOrderLinked} />
      <CancelSubscriptionDialog open={cancelDialogOpen} onClose={closeCancelDialog} organisationId={organisationId} endDate={subscriptionEndDate} alreadyEnding={ending} paused={paused} odooLinked={odooOrderLinked} />
      <RemoveCreditsModal open={removeModalOpen} onClose={closeRemoveModal} organisationId={organisationId} />
      <BillingInfoModal open={billingModalOpen} onClose={closeBillingModal} onSaved={refetchBilling} organisationId={organisationId} info={billing?.info} />
    </>
  );
};

export const CreditsModal = memo(CreditsModal_);
